// ============================================================================
// ANNOUNCEMENTS — LifeOS School.dc.html, 6a (all courses) and 6b (one course).
//
// README §3: an unread row is semibold `ink` with the course and author beneath it; a read row
// drops to medium `ink-3` and is labelled `· read`.
//
// `read` is a stored field, never inferred from age. An announcement from this morning that has
// been read is read; one from last week that has not is still bold and still carries its dot.
// Inferring it from a timestamp is how a system quietly forgets things you never looked at.
// ============================================================================

import { useId, useState, type CSSProperties } from 'react'
import type { Announcement } from '@app/shared'

import { SkeletonRows } from '../Card'
import { LinkifiedText } from '../LinkifiedText'
import { useMarkAnnouncementsRead } from '../../lib/data'
import { Speech, SpeechPlain } from './icons'
import { agoPhrase, hueBg, hueVar } from './derive'
import { Unavailable } from './Chrome'

/**
 * How tall an expanded announcement is allowed to grow before it scrolls inside itself.
 *
 * The card owns a fixed share of the School overview, and one long announcement must not push
 * the rest of the feed out of it. Nine or so lines, then the panel scrolls — the text is always
 * reachable, which is the only property that matters here.
 */
const MAX_BODY_HEIGHT = '11rem'

/**
 * The overview's card: every course, newest first, with the older ones counted not hidden — and
 * now reachable, because "Open feed" expands this card rather than pointing at a screen that does
 * not exist. `shown` is the resting count; pressing the footer lifts it to all of them.
 */
export function AnnouncementsCard({
  announcements,
  isPending,
  error,
  today,
  nowMinutes,
  shown = 4,
}: {
  announcements: Announcement[] | undefined
  isPending: boolean
  error: unknown
  today: string
  nowMinutes: number
  shown?: number
}) {
  const rows = announcements ?? []
  const unread = rows.filter((a) => !a.read).length

  const markRead = useMarkAnnouncementsRead()
  // ONE OPEN ROW AT A TIME. The card has a fixed share of the screen; two open bodies would push
  // the fourth row and the "N older" footer out of it, and a list that reflows under the cursor
  // is a list you lose your place in.
  const [openId, setOpenId] = useState<string | null>(null)
  /**
   * "OPEN FEED" — the whole feed, in this card, and that is the entire feature.
   *
   * It used to be a disabled button whose title said the screen it would open had not been built.
   * There was never a screen to build: the rows are ALREADY in hand — School.tsx calls
   * `useAnnouncements()` with no filter, which is one unfiltered read of
   * GET /api/school/announcements — and the card was slicing four off the front of them. So the
   * fix is to stop slicing, not to add a route. The list below already scrolls inside its own
   * `min-h-0` box, so a long feed lengthens the scroll and never the card.
   *
   * The count it prints when open is `rows.length`, which is literally what is on screen. The
   * button's title carries the one thing the card cannot see: the API answers with the newest 100
   * unless asked otherwise (apps/worker/src/routes/school.ts → `limit: limit ?? 100`).
   */
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? rows : rows.slice(0, shown)
  const older = rows.length - visible.length

  /** Opening an announcement is reading it. Closing one is not un-reading it — `read` is a latch. */
  function toggle(a: Announcement) {
    const opening = openId !== a.id
    setOpenId(opening ? a.id : null)
    if (opening && !a.read) markRead.mutate([a.id])
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden rounded-context border border-line bg-surface px-5 py-[17px] shadow-card">
      <header className="flex flex-none items-center gap-[9px]">
        <Speech size={17} strokeWidth={1.7} className="flex-none text-muted" />
        <h2 className="text-[13px] font-semibold leading-none">Announcements</h2>
        {unread > 0 && (
          <span className="rounded-pill bg-ink px-[7px] py-[2px] text-[10px] font-bold leading-[1.5] text-surface">
            {unread} unread
          </span>
        )}
        <button
          type="button"
          disabled={unread === 0 || markRead.isPending}
          onClick={() => markRead.mutate(rows.filter((a) => !a.read).map((a) => a.id))}
          title={
            unread === 0
              ? 'Everything here is read.'
              : 'Marks these read in LifeOS. Nothing is written to bCourses — this connector is ' +
                'read-only, so Canvas keeps its own read state and the next sync adds it to this one.'
          }
          className={[
            'ml-auto text-[11px] font-medium leading-none',
            'transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)]',
            unread === 0 || markRead.isPending
              ? 'cursor-not-allowed text-muted'
              : 'cursor-pointer text-muted hover:text-ink',
          ].join(' ')}
        >
          Mark all read
        </button>
      </header>

      {isPending && <SkeletonRows rows={4} />}
      <Unavailable error={error} />
      {announcements && rows.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy">
          No announcements have been posted in any course.
        </p>
      )}

      <div className="flex min-h-0 flex-col gap-3 overflow-y-auto">
        {visible.map((a) => (
          <AnnouncementRow
            key={a.id}
            announcement={a}
            today={today}
            nowMinutes={nowMinutes}
            open={openId === a.id}
            onToggle={() => toggle(a)}
          />
        ))}
      </div>

      {(older > 0 || expanded) && (
        <div className="mt-auto flex flex-none items-center gap-[9px] pt-[2px]">
          <span className="text-[11px] font-medium leading-none text-muted">
            {expanded
              ? `Showing all ${rows.length}`
              : `${older} older ${older === 1 ? 'announcement' : 'announcements'}`}
          </span>
          <span className="h-px flex-1 bg-fill" aria-hidden />
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            title={
              expanded
                ? 'Collapses the card back to the newest few. Nothing is un-read by collapsing it.'
                : 'Shows every announcement this card has — one read of GET /api/school/announcements, ' +
                  'which answers with the newest 100. It expands here rather than opening a screen.'
            }
            // 12d: fully-rounded shape, so hover is ink filling from the left, in the hue the
            // control already wears. Press is the app-wide 1px, plus this class's shadow collapse.
            style={{ '--mo-rule': 'var(--color-success-dot)' } as CSSProperties}
            className="mo-ink mo-press flex-none cursor-pointer whitespace-nowrap rounded-pill px-2 py-[3px] text-[11px] font-medium leading-none text-success-dot"
          >
            {expanded ? 'Collapse' : 'Open feed'}
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * ONE ROW, and now a button — the defect it fixes is that it was not one.
 *
 * COLLAPSED it is exactly what README §3 draws: unread is semibold `ink` over the course and
 * author, read drops to medium `ink-3` and is labelled `· read`. EXPANDED (the handoff draws no
 * expanded state, so this is the feed's own idiom rather than a mockup being followed) the row
 * keeps its header and grows the full body underneath it, in the same type as the summary line.
 *
 * THE BODY IS TEXT, NOT MARKUP. The Worker turns Canvas's HTML into plain text before it reaches
 * the wire (worker/src/lib/html-text.ts), which is why `whitespace-pre-line` is safe and correct
 * here: the newlines it keeps are the professor's paragraphs, and React escapes the rest.
 *
 * AND THE URLs IN IT ARE REAL LINKS (owner screenshot, 2026-08-29: a GSI's Zoom room and a Google
 * Forms sign-up, both dead text). `LinkifiedText` splits the SAME plain text into text and link
 * spans and renders React elements from them — no markup is re-emitted, the sanitizer upstream is
 * untouched, and the expanded panel is the button's SIBLING rather than its child, which is what
 * keeps an anchor out of the middle of a button.
 */
function AnnouncementRow({
  announcement: a,
  today,
  nowMinutes,
  open,
  onToggle,
}: {
  announcement: Announcement
  today: string
  nowMinutes: number
  open: boolean
  onToggle: () => void
}) {
  const bodyId = useId()
  const summary = a.read
    ? `${a.courseCode} · read`
    : `${a.courseCode}${a.author ? ` · ${a.author}` : ''} — “${a.body}”`

  return (
    <article
      style={{ '--mo-rule': hueVar(a.courseCode) } as CSSProperties}
      className={['mo-rule rounded-row', a.read ? '' : 'bg-surface-2'].join(' ')}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full gap-[11px] rounded-row px-3 py-[10px] text-left"
      >
        <span
          className={[
            'mt-[5px] size-[7px] flex-none rounded-full',
            'transition-colors duration-[var(--dur-base)] ease-[var(--ease-out)]',
            a.read ? 'bg-line-2' : hueBg(a.courseCode),
          ].join(' ')}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[9px]">
            <h3
              className={[
                'text-[12.5px] leading-[1.2] text-pretty',
                a.read ? 'font-medium text-ink-3' : 'font-semibold text-ink',
              ].join(' ')}
            >
              {a.title}
            </h3>
            <span
              className={[
                'ml-auto whitespace-nowrap text-[10.5px] font-normal leading-none',
                a.read ? 'text-faintest' : 'text-microcopy',
              ].join(' ')}
            >
              {agoPhrase(a.postedAt, today, nowMinutes)}
            </span>
          </div>
          {/* A read row keeps its course and drops its body — it has already been read, and the
              list's job from then on is to say it exists, not to say it again. Opening it is how
              you ask for the body back. */}
          <p
            className={[
              'mt-1 text-[11.5px] font-normal leading-[1.45] text-pretty',
              open ? 'line-clamp-none' : 'line-clamp-2',
              a.read ? 'text-microcopy' : 'text-ink-3',
            ].join(' ')}
          >
            {open ? `${a.courseCode}${a.author ? ` · ${a.author}` : ''}` : summary}
          </p>
        </div>
      </button>

      {/*
        THE MOTION: max-height, between zero and a cap the panel is allowed to scroll inside.

        Two tidier idioms were tried first and both were dropped: the `0fr → 1fr` grid trick,
        whose flexible track has to resolve against free space that two nested `min-h-0` flex
        columns do not offer, and a `scrollHeight` read during render, which measures the box
        while it is still clipped. `max-height` depends on neither — it is a plain cap on a
        plain block — and the ONE thing a constant cap can get wrong, clipping a long
        announcement, is answered by letting the panel scroll rather than by guessing higher.
        Nothing is ever hidden without a way to reach it. `dur-base` rather than `dur-fast`:
        this is a panel opening, not a hover.

        NOT PHOTOGRAPHED. The interaction is asserted in routes/interactions.smoke.test.tsx and
        was stepped through in a real browser at 1440px (aria-expanded, the applied style, the
        body text, the unread count) — but the agent's browser pane does not composite frames,
        so how this actually MOVES has been reasoned about and not seen. Same class of unknown
        the ship gate prints at the end of every run.
      */}
      <div
        id={bodyId}
        aria-hidden={!open}
        className={[
          'transition-[max-height,opacity] duration-[var(--dur-base)] ease-[var(--ease-out)]',
          'motion-reduce:transition-none',
          open ? 'overflow-y-auto' : 'overflow-hidden',
        ].join(' ')}
        style={{ maxHeight: open ? MAX_BODY_HEIGHT : 0, opacity: open ? 1 : 0 }}
      >
        <p className="px-3 pb-[11px] pl-[26px] text-[11.5px] font-normal leading-[1.55] text-ink-3 whitespace-pre-line text-pretty">
          {/* An announcement with a subject and no message is a real Canvas row. Saying so
              beats an empty panel that looks like something failed to load. */}
          {a.body.trim() ? (
            <LinkifiedText text={a.body.trim()} />
          ) : (
            'This announcement has a subject and no message.'
          )}
        </p>
      </div>
    </article>
  )
}

/** How many rows the panel shows before "N more" is asked to reveal the rest. */
const COURSE_ROWS_COLLAPSED = 3

/**
 * THE COURSE PAGE'S PANEL — 6b, and every announcement in it is readable.
 *
 * WHAT IT USED TO DO (owner, 2026-09-08: "I only see the most recent announcement, so I cannot
 * click on older announcements ... '4 more' does not let me click on it"). The newest was printed
 * with its body, the next two were TITLES IN A PLAIN DIV with no handler on them, and the count
 * was a `<span>`. So a course with seven announcements had six that could not be read anywhere in
 * the app — not a layout problem, a hole in the product: the data was synced, stored and served,
 * and the only surface that showed it showed one.
 *
 * IT NOW USES THE FEED'S OWN ROW. `AnnouncementRow` already had all of it — a real button, the
 * aria-expanded/aria-controls pair, the body that scrolls inside itself rather than pushing the
 * panel apart, the linkified URLs, and marking read on open. Reusing it means the two surfaces
 * cannot drift into behaving differently about the same announcement, which is the more valuable
 * half of this change.
 *
 * THE NEWEST STILL OPENS ON ARRIVAL, because that is what this panel already did. Opening by
 * default deliberately does NOT mark it read: `read` is a latch set by a deliberate press, and a
 * panel that marked things read by being looked at would quietly drain the unread count from the
 * corner of the screen.
 */
export function CourseAnnouncementsPanel({
  announcements,
  isPending,
  error,
  today,
  nowMinutes,
}: {
  announcements: Announcement[] | undefined
  isPending: boolean
  error: unknown
  today: string
  nowMinutes: number
}) {
  const rows = announcements ?? []
  const unread = rows.filter((a) => !a.read).length

  const markRead = useMarkAnnouncementsRead()
  const [showAll, setShowAll] = useState(false)
  /**
   * `undefined` means nobody has pressed anything yet, and the newest is open. `null` means every
   * row is deliberately closed. Two states rather than one, because `openId ?? newest` would make
   * the lead impossible to close — closing it would fall straight back to opening it.
   */
  const [openId, setOpenId] = useState<string | null | undefined>(undefined)
  const openRowId = openId === undefined ? (rows[0]?.id ?? null) : openId

  /** Opening an announcement is reading it. Closing one is not un-reading it. */
  function toggle(a: Announcement) {
    const opening = openRowId !== a.id
    setOpenId(opening ? a.id : null)
    if (opening && !a.read) markRead.mutate([a.id])
  }

  const visible = showAll ? rows : rows.slice(0, COURSE_ROWS_COLLAPSED)
  const more = rows.length - visible.length
  const collapsible = rows.length > COURSE_ROWS_COLLAPSED

  return (
    <section className="flex flex-none flex-col gap-[10px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex items-center gap-[9px]">
        <SpeechPlain size={16} strokeWidth={1.7} className="flex-none text-muted" />
        <h2 className="text-[13px] font-semibold leading-none">Announcements</h2>
        {unread > 0 && (
          <span className="rounded-pill bg-ink px-[7px] py-[2px] text-[10px] font-bold leading-[1.5] text-surface">
            {unread} unread
          </span>
        )}
      </header>

      {isPending && <SkeletonRows rows={2} />}
      <Unavailable error={error} />
      {announcements && rows.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy">
          Nothing has been posted in this course.
        </p>
      )}

      {visible.map((a) => (
        <AnnouncementRow
          key={a.id}
          announcement={a}
          today={today}
          nowMinutes={nowMinutes}
          open={openRowId === a.id}
          onToggle={() => toggle(a)}
        />
      ))}

      {/* A BUTTON, because the thing it names is reachable — the rest of the list is three lines
          below. The overview's footer now works the same way ("Open feed" expands that card in
          place); the two controls differ only in their wording, not in what they do. */}
      {collapsible && (
        <div className="flex flex-none items-center gap-[9px] pt-[2px]">
          <span className="h-px flex-1 bg-fill" aria-hidden />
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="flex-none whitespace-nowrap rounded-[3px] text-[10.5px] font-semibold leading-none text-success-dot outline-none hover:underline focus-visible:ring-2 focus-visible:ring-line-2"
          >
            {showAll ? 'Show fewer' : `Show ${more} more`}
          </button>
        </div>
      )}
    </section>
  )
}
